"use client";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, Switch } from "@mui/material";
import React, { useState } from "react";

import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { getRoutingTypeLabel } from "@/components/templates/ServiceQueueForm/form-data";
import { SERVICE_QUEUE_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useUpdateServiceQueue from "@/hooks/requests/service-queue/use-update-service-queue";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import IServiceQueue from "@/interfaces/service-queue.interface";
import { ServiceQueueMessages } from "@/messages/service-queue/service-queue.messages";
import serviceQueueService from "@/services/service-queue.service";

import { ServiceQueuePopover } from "../Popover";

interface Props {
  serviceQueue: IServiceQueue;
  messages: ServiceQueueMessages;
  index: number;
}

export function ServiceQueueTableRow({ serviceQueue, index, messages }: Props) {
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IServiceQueue>();
  const { update } = useUpdateServiceQueue();
  const { mutate } = useMutate();
  const hasNoPermissionToEdit = useHasNoPermission(SERVICE_QUEUE_PERMISSIONS.EDIT);
  const [isToggling, setIsToggling] = useState(false);

  const handleToggleActive = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (hasNoPermissionToEdit || isToggling) return;

    setIsToggling(true);
    try {
      // Listagem não traz usuários; busca o detalhe para não apagar os vínculos no update.
      const fullQueue = await serviceQueueService.getById(serviceQueue.id);
      await update({
        _id: serviceQueue.id,
        formData: {
          name: fullQueue.name,
          active: !serviceQueue.active,
          routingType: fullQueue.routingType,
          sla: fullQueue.sla,
          users: (fullQueue.users || []).map((user) => ({ userID: user.userID })),
        },
        successMessage: messages.updateServiceQueueSuccess,
        errorMessage: messages.updateServiceQueueError,
        onSuccess: () => {
          void mutate(SWR_KEYS.getServiceQueues);
          void mutate(SWR_KEYS.getServiceQueueById);
        },
      });
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <TableRow index={index}>
      <TableCell>{serviceQueue.id}</TableCell>
      <TableCell>{serviceQueue.name}</TableCell>
      <TableCell>
        <Switch
          checked={!!serviceQueue.active}
          disabled={hasNoPermissionToEdit || isToggling}
          onClick={handleToggleActive}
        />
      </TableCell>
      <TableCell>{getRoutingTypeLabel(serviceQueue.routingType, messages)}</TableCell>
      <TableCell>{serviceQueue.sla ?? "-"}</TableCell>
      <TableCell>
        <IconButton onClick={(e) => openPopover(e, serviceQueue)}>
          <MoreHorizIcon />
        </IconButton>

        {selected && (
          <ServiceQueuePopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            selected={selected}
          />
        )}
      </TableCell>
    </TableRow>
  );
}
